import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Select } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { fetchActiveProducts } from '@/redux/actions/productActions';

/**
 * ProductSelect Component (Function Component + Redux Hooks)
 *
 * - Tìm kiếm sản phẩm (server-side) + infinite scroll
 * - label hiển thị: Tên (bold) + phần phụ (italic) giống BaseSelect
 * - Có title để filterOption dùng text
 * - labelInValue để tương thích Form.create / getFieldDecorator
 */
const ProductSelect = ({
    value,
    onChange,
    defaultText,
    isLoading: propLoading = false,

    // mapping field giống BaseSelect
    optionValue = 'value',
    optionLabel = 'label',
    additionalLabel1,
    additionalLabel2,
    additionalLabel3,
    additionalLabel4,

    placeholder = 'Tìm sản phẩm...',
    ...restProps
}) => {
    const dispatch = useDispatch();
    const [keyword, setKeyword] = useState('');

    // TODO: đổi đúng reducer của bạn
    const options = useSelector(state => state.productReducer.activeList.items);
    const pagination = useSelector(state => state.productReducer.activeList.pagination);
    const isLoading = useSelector(state => state.productReducer.activeList.loading) || propLoading;

    useEffect(() => {
        dispatch(fetchActiveProducts({ keyword: '', page: 1 }));
    }, [dispatch]);

    const handleSearch = useCallback((val) => {
        setKeyword(val);
        dispatch(fetchActiveProducts({ keyword: val, page: 1 }));
    }, [dispatch]);

    const handleScroll = useCallback(() => {
        const nextPage = (pagination?.currentPage || 1) + 1;
        dispatch(fetchActiveProducts({ keyword, page: nextPage }));
    }, [dispatch, keyword, pagination]);

    const buildExtraText = useCallback((option) => {
        const addLabel1 = additionalLabel1 ? (option?.[additionalLabel1] ?? 0) : null;      // số lượng
        const addLabel2 = additionalLabel2 ? (option?.[additionalLabel2] ?? ' ') : null;     // hạn sử dụng
        const addLabel3 = additionalLabel3
            ? (option?.[additionalLabel3] ? `, ${option?.[additionalLabel3]}` : 'Kho: ')
            : null;
        // giữ đúng logic BaseSelect: chỉ cần 1 trong 3 label phụ thì mới show
        if (!additionalLabel1 && !additionalLabel2 && !additionalLabel3 && !additionalLabel4) return '';

        // bạn đang dùng format này:
        // (Số lượng: X, Hạn sử dụng: YKho: Z)  // (bỏ VAT)
        // => mình giữ y hệt.
        const qtyText = additionalLabel1 ? `Số lượng: ${addLabel1}` : null;
        const expText = additionalLabel2 ? `Hạn sử dụng: ${addLabel2}` : null;
        const stockText = additionalLabel3 ? `${addLabel3}` : null;

        const parts = [qtyText, expText, stockText].filter(Boolean);
        return parts.length ? `(${parts.join(', ')})` : '';
    }, [additionalLabel1, additionalLabel2, additionalLabel3, additionalLabel4]);

    const formatOption = useCallback((option, index) => {
        const name = option?.[optionLabel] ?? '';
        const hh = buildExtraText(option);
        const title = `${name} ${hh}`.trim();

        return {
            key: `${option?.[optionValue]}_${index}`,
            value: option?.[optionValue],
            label: (
                <span>
                    <span style={{ fontWeight: 'bold' }}>{name}</span>
                    {hh ? <span style={{ fontStyle: 'italic' }}> {hh}</span> : null}
                </span>
            ),
            title,
        };
    }, [optionValue, optionLabel, buildExtraText]);

    // merge selected nếu không có trong options (giữ selected khi edit)
    const selectedId = value?.value;
    const selectedLabel = value?.label;
    const exists = options?.some(opt => opt?.[optionValue] === selectedId);

    const mergedOptions = useMemo(() => {
        if (!selectedId || exists) return options || [];
        // chỉ cần nhét label tối thiểu để hiển thị
        const temp = { [optionValue]: selectedId, [optionLabel]: selectedLabel };
        return [temp].concat(options || []);
    }, [options, exists, selectedId, selectedLabel, optionValue, optionLabel]);

    return (
        <Select
            showSearch
            allowClear
            labelInValue
            value={value ?? null}
            loading={isLoading}
            placeholder={placeholder}
            filterOption={(input, option) =>
                (option?.title || '').toLowerCase().includes((input || '').toLowerCase())
            }
            onSearch={handleSearch}
            onPopupScroll={handleScroll}
            // nếu bạn muốn server-side search chuẩn như CustomerSelect:
            // filterOption={false}
            onChange={(selected) => {
                if (onChange) {
                    onChange(selected?.value ?? null, selected?.label ?? null);
                }

                if (selected === null) {
                    setKeyword('');
                    dispatch(fetchActiveProducts({ keyword: '', page: 1 }));
                }
            }}
            options={[
                ...(defaultText ? [{ value: null, label: defaultText, title: defaultText }] : []),
                ...mergedOptions.map(formatOption),
            ]}
            {...restProps}
        />
    );
};

ProductSelect.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    defaultText: PropTypes.string,
    isLoading: PropTypes.bool,

    optionValue: PropTypes.string,
    optionLabel: PropTypes.string,
    additionalLabel1: PropTypes.string,
    additionalLabel2: PropTypes.string,
    additionalLabel3: PropTypes.string,
    additionalLabel4: PropTypes.string,

    placeholder: PropTypes.string,
};

export default ProductSelect;
